"use strict";

// Shared cart cache to track quantities across all product forms
let cartCache = null;
let cartCachePromise = null;

// Update cart cache from cart data
function updateCartCache(cartData) {
  if (cartData && cartData.items) {
    cartCache = {};
    cartData.items.forEach((item) => {
      cartCache[item.variant_id] = item.quantity;
    });
  }
}

// Get cart quantity - use cache first, fallback to API
async function getCartQuantity(variantId) {
  // If we have cache, use it
  if (cartCache && cartCache.hasOwnProperty(variantId)) {
    return cartCache[variantId];
  }

  // If cache fetch is already in progress, wait for it
  if (cartCachePromise) {
    await cartCachePromise;
    if (cartCache && cartCache.hasOwnProperty(variantId)) {
      return cartCache[variantId];
    }
  }

  // Fetch cart and update cache
  cartCachePromise = (async () => {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      updateCartCache(cart);
      return (
        cart.items.find((item) => item.variant_id === parseInt(variantId))
          ?.quantity || 0
      );
    } catch (error) {
      console.error("Error fetching cart:", error);
      return 0;
    } finally {
      cartCachePromise = null;
    }
  })();

  return await cartCachePromise;
}

// Subscribe to cart updates to keep cache in sync
if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
  subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
    // Update cache when cart is updated
    if (data.variant && data.variant.items) {
      updateCartCache(data.variant);
    }
  });
}

function initProductForm(form) {
  if (!form) return;
  
  // Skip if already initialized (unless forced)
  if (form.hasAttribute("data-form-initialized")) {
    return;
  }

  // Get or set form ID
  let formId = form.id || form.getAttribute("id");
  if (!formId) {
    formId = "product-form-" + Date.now();
    form.id = formId;
  }

  // Find script elements inside the form (more reliable than by ID)
  const productDataEl = form.querySelector('script[id^="product-data-"]');
  const variantDataEl = form.querySelector('script[id^="variant-data-"]');

  if (!productDataEl || !variantDataEl) {
    return;
  }

  const productData = JSON.parse(productDataEl.textContent || "{}");
  const currentVariant = JSON.parse(variantDataEl.textContent || "{}");

  const quantitySelector = form.querySelector(".product-quantity_selector");
  const quantityInput = form.querySelector(".quantity-input");
  const quantityDown = form.querySelector(".quantity-down");
  const quantityUp = form.querySelector(".quantity-up");
  const addToCartBtn = form.querySelector(".add-to-cart");
  const loadingOverlay = form.querySelector(".loading-spinner-overlay");
  const loadingFill = form.querySelector(".loading-fill");
  const errorMessage =
    form.querySelector(".product-error-message") ||
    document.getElementById("error-message-" + formId);
  // Set ID if it doesn't have one
  if (errorMessage && !errorMessage.id) {
    errorMessage.id = "error-message-" + formId;
  }

  if (!addToCartBtn || !loadingOverlay || !loadingFill) {
    return;
  }

  // Handle sold-out state
  const isSoldOut = !currentVariant.available;
  if (isSoldOut) {
    // Hide quantity selector if it exists
    if (quantitySelector) {
      quantitySelector.style.display = "none";
    }
    // Disable quantity buttons if they exist
    if (quantityDown) {
      quantityDown.disabled = true;
    }
    if (quantityUp) {
      quantityUp.disabled = true;
    }
    // Disable submit button
    addToCartBtn.disabled = true;
  } else {
    // If quantity elements don't exist for available items, skip quantity functionality
    if (!quantityInput || !quantityDown || !quantityUp) {
      return;
    }
  }

  let errorTimeout = null;

  // Helper function to get current quantity from span
  function getQuantity() {
    if (!quantityInput) return 1;
    return (
      parseInt(
        quantityInput.textContent || quantityInput.dataset.quantity || "1"
      ) || 1
    );
  }

  // Helper function to set quantity in span
  function setQuantity(value) {
    if (!quantityInput) return;
    quantityInput.textContent = value;
    quantityInput.dataset.quantity = value;
  }

  // Quantity selector functionality (only if not sold out and elements exist)
  if (!isSoldOut && quantityInput && quantityDown && quantityUp) {
    // Remove old event listeners if they exist (to prevent duplicates)
    const oldDownHandler = quantityDown._quantityDownHandler;
    const oldUpHandler = quantityUp._quantityUpHandler;
    
    if (oldDownHandler) {
      quantityDown.removeEventListener("click", oldDownHandler);
    }
    if (oldUpHandler) {
      quantityUp.removeEventListener("click", oldUpHandler);
    }
    
    // Create new handlers
    const quantityDownHandler = function () {
      const currentValue = getQuantity();
      if (currentValue > 1) {
        setQuantity(currentValue - 1);
        hideError();
      }
    };
    
    const quantityUpHandler = function () {
      const currentValue = getQuantity();
      const maxValue = quantityInput.dataset.max
        ? parseInt(quantityInput.dataset.max)
        : 999;
      if (!maxValue || currentValue < maxValue) {
        setQuantity(currentValue + 1);
        hideError();
      } else {
        showError(`Maximum quantity available is ${maxValue}.`);
      }
    };
    
    // Store handler references for future removal
    quantityDown._quantityDownHandler = quantityDownHandler;
    quantityUp._quantityUpHandler = quantityUpHandler;
    
    // Add new event listeners
    quantityDown.addEventListener("click", quantityDownHandler);
    quantityUp.addEventListener("click", quantityUpHandler);
  }

  function showError(message) {
    if (!errorMessage) return;
    if (errorTimeout) clearTimeout(errorTimeout);
    errorMessage.textContent = message;
    errorMessage.classList.add("show");
    errorTimeout = setTimeout(hideError, 5000);
  }

  function hideError() {
    if (!errorMessage) return;
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      errorTimeout = null;
    }
    errorMessage.classList.remove("show");
  }

  async function checkQuantityAvailability(quantity) {
    // First check if variant is available
    if (!currentVariant.available) {
      return {
        valid: false,
        message: "This item is out of stock.",
      };
    }

    const variantId = currentVariant.id;

    // Skip inventory check if inventory is not managed or policy allows overselling
    if (
      !currentVariant.inventory_management ||
      currentVariant.inventory_management !== "shopify" ||
      currentVariant.inventory_policy === "continue"
    ) {
      return { valid: true };
    }

    // Only check inventory if it's being tracked and policy is 'deny'
    const maxStock = currentVariant.inventory_quantity;

    // If inventory_quantity is null/undefined, inventory tracking might be off - skip check
    if (maxStock === null || maxStock === undefined) {
      return { valid: true };
    }

    const cartQuantity = await getCartQuantity(variantId);
    const availableQuantity = maxStock - cartQuantity;

    if (availableQuantity <= 0) {
      return {
        valid: false,
        message:
          cartQuantity > 0
            ? `You already have all ${cartQuantity} available items in your cart.`
            : "This item is out of stock.",
      };
    }

    if (quantity > maxStock) {
      return {
        valid: false,
        message: `Only ${maxStock} items available in stock.`,
      };
    }

    if (quantity > availableQuantity) {
      return {
        valid: false,
        message: `You already have ${cartQuantity} in your cart. Only ${availableQuantity} more available.`,
      };
    }

    return { valid: true };
  }

  // Loading animation functions
  function startLoading() {
    loadingOverlay.classList.add("loading");
    loadingFill.style.animation = "moveDown 0.3s ease-out forwards";
    addToCartBtn.disabled = true;
    // Show spinner after move down completes
    setTimeout(() => {
      loadingOverlay.classList.add("spinning");
    }, 300);
  }

  function finishLoading() {
    loadingOverlay.classList.remove("spinning");
    loadingFill.style.animation = "revealUp 0.4s ease-out forwards";
    setTimeout(() => {
      loadingOverlay.classList.remove("loading");
      loadingFill.style.animation = "";
      addToCartBtn.disabled = false;
    }, 400);
  }

  // Check if this is a buy now form
  const isBuyNow =
    form.hasAttribute("data-buy-now") &&
    form.getAttribute("data-buy-now") === "true";

  // Remove old form submit listener if it exists (to prevent duplicates)
  const oldSubmitHandler = form._formSubmitHandler;
  if (oldSubmitHandler) {
    form.removeEventListener("submit", oldSubmitHandler);
  }

  // Form submission handler
  const formSubmitHandler = async function (e) {
    e.preventDefault();

    // Prevent submission if sold out
    if (!currentVariant.available) {
      return;
    }

    const quantity = getQuantity();

    // Validate quantity
    const quantityCheck = await checkQuantityAvailability(quantity);
    if (!quantityCheck.valid) {
      showError(quantityCheck.message);
      return;
    }

    // BUY NOW: Clear cart first, then add only current item and redirect to checkout
    if (isBuyNow) {
      startLoading();
      hideError();

      try {
        // Step 1: Clear the cart first
        const clearResponse = await fetch("/cart/clear.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        if (!clearResponse.ok) {
          throw new Error("Failed to clear cart");
        }

        // Step 2: Create a form to add item and redirect to checkout (like product.liquid)
        const checkoutForm = document.createElement("form");
        checkoutForm.method = "POST";
        checkoutForm.action = "/cart/add";

        // Add main product
        const mainProductInput = document.createElement("input");
        mainProductInput.type = "hidden";
        mainProductInput.name = "items[0][id]";
        mainProductInput.value = currentVariant.id;
        checkoutForm.appendChild(mainProductInput);

        const mainProductQty = document.createElement("input");
        mainProductQty.type = "hidden";
        mainProductQty.name = "items[0][quantity]";
        mainProductQty.value = quantity;
        checkoutForm.appendChild(mainProductQty);

        // Add redirect to checkout
        const returnToInput = document.createElement("input");
        returnToInput.type = "hidden";
        returnToInput.name = "return_to";
        returnToInput.value = "/checkout";
        checkoutForm.appendChild(returnToInput);

        document.body.appendChild(checkoutForm);

        // Small delay to ensure cart is cleared, then submit form
        setTimeout(() => {
          checkoutForm.submit();
        }, 100);
      } catch (error) {
        console.error("Error:", error);
        finishLoading();
        showError(
          error.message || "Failed to process buy now. Please try again."
        );

        if (
          typeof publish !== "undefined" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          publish(PUB_SUB_EVENTS.cartError, {
            error: error.message,
            context: "buy-now",
            productId: productData.id,
          });
        }
      }
      return;
    }

    // REGULAR ADD TO CART: Use fetch API
    startLoading();
    hideError();

    try {
      if (!window.routes || !window.routes.cart_add_url) {
        throw new Error("Cart routes not available");
      }

      const cartUrl = window.routes.cart_add_url + ".js";
      const response = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: currentVariant.id,
              quantity: quantity,
            },
          ],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update cart cache from the response
        updateCartCache(data);

        if (
          typeof publish !== "undefined" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            formData: {
              id: currentVariant.id,
              quantity: quantity,
            },
            product: productData,
          });
        }
        finishLoading();
      } else {
        throw new Error(data.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error:", error);
      finishLoading();
      showError(
        error.message || "Failed to add product to cart. Please try again."
      );

      if (
        typeof publish !== "undefined" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "add-to-cart",
          productId: productData.id,
        });
      }
    }
  };
  
  // Store handler reference for future removal
  form._formSubmitHandler = formSubmitHandler;
  
  // Add form submit listener
  form.addEventListener("submit", formSubmitHandler);
  
  // Mark form as initialized
  form.setAttribute("data-form-initialized", "true");
}

// Reset loading state for a product form
function resetProductFormLoading(form) {
  if (!form) return;
  
  const loadingOverlay = form.querySelector(".loading-spinner-overlay");
  const loadingFill = form.querySelector(".loading-fill");
  const addToCartBtn = form.querySelector(".add-to-cart");
  const quantityDown = form.querySelector(".quantity-down");
  const quantityUp = form.querySelector(".quantity-up");
  
  // Reset loading overlay
  if (loadingOverlay) {
    loadingOverlay.classList.remove("loading", "spinning");
  }
  
  // Reset loading fill animation
  if (loadingFill) {
    loadingFill.style.animation = "";
  }
  
  // Re-enable buttons (unless form is sold out)
  const variantDataEl = form.querySelector('script[id^="variant-data-"]');
  if (variantDataEl) {
    try {
      const currentVariant = JSON.parse(variantDataEl.textContent || "{}");
      const isSoldOut = !currentVariant.available;
      
      if (!isSoldOut) {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
        }
        if (quantityDown) {
          quantityDown.disabled = false;
        }
        if (quantityUp) {
          quantityUp.disabled = false;
        }
      }
    } catch (e) {
      // If parsing fails, just enable the button
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
      }
    }
  } else {
    // If no variant data, just enable the button
    if (addToCartBtn) {
      addToCartBtn.disabled = false;
    }
  }
}

// Reset loading state for all product forms
function resetAllProductFormsLoading() {
  const productForms = document.querySelectorAll("[data-product-form]");
  productForms.forEach((form) => {
    resetProductFormLoading(form);
  });
}

// Make functions globally available
window.initProductForm = initProductForm;
window.resetProductFormLoading = resetProductFormLoading;
window.resetAllProductFormsLoading = resetAllProductFormsLoading;

// Initialize all product forms on page load
document.addEventListener("DOMContentLoaded", function () {
  const productForms = document.querySelectorAll("[data-product-form]");
  productForms.forEach((form) => {
    initProductForm(form);
  });
});

// Also listen for preloaderFinished event to reinitialize on page restore
document.addEventListener("preloaderFinished", function () {
  // Reset loading states first
  resetAllProductFormsLoading();
  
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    const productForms = document.querySelectorAll("[data-product-form]");
    productForms.forEach((form) => {
      // Reinitialize form (will skip if already initialized)
      initProductForm(form);
    });
  }, 100);
});
